import React, { useState } from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    TextField,
    DateField,
    ReferenceField,
    ReferenceArrayInput,
    AutocompleteArrayInput,
    SelectInput,
    required,
    useTranslate,
    Toolbar,
    SaveButton,
    useRedirect,
    useNotify,
    useRefresh,
    useRecordContext,
    useDataProvider,
} from 'react-admin';
import {
    Box,
    Typography,
    Card,
    CardContent,
    IconButton,
    Switch,
    FormControlLabel,
    FormHelperText,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from '@mui/material';
import { ArrowBack, Storefront, ElectricBolt, Info, VpnKey, Warning } from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';
import { apiClient } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';
import { EnrollmentCodeDialog } from './EnrollmentCodeDialog';
import { HealthBadges } from './KioskDevicesList';
import { KioskSettingsPanel } from './KioskSettingsPanel';
import { OctopusBlockedPanel } from './OctopusBlockedPanel';
import { KioskUpdatePanel } from './KioskUpdatePanel';
import { PrinterDetailLine } from './PrinterDetailLine';
import { KioskPrintLogPanel } from './KioskPrintLogPanel';
import { OctopusReaderPanel } from './OctopusReaderPanel';
import { KioskRecentAlertsPanel } from '../kiosk-alerts/KioskRecentAlertsPanel';

const KIOSK_KNOWN_FIELDS = new Set(['siteId', 'name', 'nearbyChargerIds', 'octopusDeviceId', 'locationLabel', 'status']);

const mapKioskBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const kioskFieldLabelKey = (field: string): string =>
    KIOSK_KNOWN_FIELDS.has(field)
        ? `resources.kiosk-devices.fields.${field}`
        : field;

const kioskErrorHeader = (apiError: any, translate: (key: string, options?: any) => string): string => {
    const kioskErrorMessage = apiError?.errorCode
        ? translate(`resources.kiosk-devices.errors.${apiError.errorCode}`, { _: '' })
        : '';
    return kioskErrorMessage || translate(
        apiError?.errorCode ? `errors.${apiError.errorCode}` : 'validation.formHasErrors',
        { _: apiError?.message || 'Error' }
    );
};

const EditToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{ backgroundColor: '#00C853', '&:hover': { backgroundColor: '#00A843' } }}
        />
    </Toolbar>
);

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 2, sm: 4 } }}>
            <IconButton
                onClick={() => redirect('/kiosk-devices')}
                size="large"
                sx={{ flexShrink: 0, backgroundColor: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5 }}>
                    {translate('resources.kiosk-devices.edit.title')}
                </Typography>
            </Box>
        </Box>
    );
};

const NearbyChargersInput = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;

    return (
        <ReferenceArrayInput source="nearbyChargerIds" reference="chargers" filter={{ siteId: record.siteId }}>
            <AutocompleteArrayInput
                optionText="name"
                label={translate('resources.kiosk-devices.fields.nearbyChargerIds')}
                fullWidth
                helperText={translate('resources.kiosk-devices.create.nearbyChargersHelperText')}
            />
        </ReferenceArrayInput>
    );
};

const StatusInput = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    return (
        <SelectInput
            source="status"
            label={translate('resources.kiosk-devices.fields.status')}
            choices={[
                { id: 'active', name: translate('resources.kiosk-devices.status.active') },
                { id: 'disabled', name: translate('resources.kiosk-devices.status.disabled') },
            ]}
            disabled={record?.status === 'pending'}
            fullWidth
        />
    );
};

interface MaintenanceSwitchProps {
    onChanged: () => void;
}

const MaintenanceSwitch: React.FC<MaintenanceSwitchProps> = ({ onChanged }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const [saving, setSaving] = useState(false);
    const [checked, setChecked] = useState<boolean>(Boolean(record?.maintenanceEnabled));

    React.useEffect(() => {
        setChecked(Boolean(record?.maintenanceEnabled));
    }, [record?.maintenanceEnabled]);

    if (!record) return null;

    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const next = event.target.checked;
        setChecked(next);
        setSaving(true);
        try {
            await apiClient.put(`${API_CONFIG.endpoints.backend.kioskDevices}/${record.id}/settings/maintenance.enabled`, { value: next });
            notify(translate(next ? 'resources.kiosk-devices.maintenance.enabled' : 'resources.kiosk-devices.maintenance.disabled'), { type: 'success' });
            refresh();
            onChanged();
        } catch (error: any) {
            setChecked(Boolean(record.maintenanceEnabled));
            notify(kioskErrorHeader(error, translate), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <FormControlLabel
                control={<Switch checked={checked} onChange={handleChange} disabled={saving} />}
                label={translate('resources.kiosk-devices.maintenance.switchLabel')}
            />
            <FormHelperText>{translate('resources.kiosk-devices.maintenance.helperText')}</FormHelperText>
        </Box>
    );
};

const StatusReadOnlyCard = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Info sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.kiosk-devices.sections.status')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <HealthBadges record={record} />
                    <PrinterDetailLine health={record.health} />

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">{translate('resources.kiosk-devices.fields.updateStateAt')}</Typography>
                            {record.updateStateAt ? <DateField source="updateStateAt" showTime /> : <Typography variant="body2">-</Typography>}
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">{translate('resources.kiosk-devices.fields.lastHeartbeatAt')}</Typography>
                            {record.lastHeartbeatAt ? <DateField source="lastHeartbeatAt" showTime /> : <Typography variant="body2">-</Typography>}
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">{translate('resources.kiosk-devices.fields.settlementDueSince')}</Typography>
                            {record.settlementDueSince ? <DateField source="settlementDueSince" showTime /> : <Typography variant="body2">-</Typography>}
                        </Box>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

interface EnrollmentSectionProps {
    onCodeIssued: (code: string, expiresAt: string | null) => void;
}

const EnrollmentSection: React.FC<EnrollmentSectionProps> = ({ onCodeIssued }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [issuing, setIssuing] = useState(false);

    if (!record) return null;

    const handleIssue = async () => {
        setIssuing(true);
        try {
            const response = await dataProvider.create('kiosk-devices', {
                data: {},
                meta: { path: `kiosk-devices/${record.id}/enrollment-code` },
            });
            const responseData = response.data as any;
            notify(translate('resources.kiosk-devices.edit.codeIssued'), { type: 'success' });
            setConfirmOpen(false);
            onCodeIssued(responseData.enrollmentCode, responseData.enrollmentExpiresAt ?? null);
            refresh();
        } catch (error: any) {
            notify(kioskErrorHeader(error, translate), { type: 'error' });
        } finally {
            setIssuing(false);
        }
    };

    return (
        <Card sx={{ overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <VpnKey sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.kiosk-devices.sections.enrollment')}
                    </Typography>
                </Box>

                <Button
                    variant="outlined"
                    startIcon={<VpnKey />}
                    onClick={() => setConfirmOpen(true)}
                    sx={{ textTransform: 'none' }}
                >
                    {translate('resources.kiosk-devices.edit.issueCode')}
                </Button>

                <Dialog open={confirmOpen} onClose={() => !issuing && setConfirmOpen(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Warning sx={{ color: '#D32F2F' }} />
                            {translate('resources.kiosk-devices.edit.issueCode')}
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2">{translate('resources.kiosk-devices.edit.issueCodeConfirm')}</Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setConfirmOpen(false)} disabled={issuing} sx={{ textTransform: 'none' }}>
                            {translate('ra.action.cancel')}
                        </Button>
                        <Button
                            onClick={handleIssue}
                            variant="contained"
                            color="error"
                            disabled={issuing}
                            startIcon={issuing ? <CircularProgress size={16} /> : <VpnKey />}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {translate('resources.kiosk-devices.edit.issueCode')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
};

interface SettingsSectionProps {
    reloadKey: number;
}

const SettingsSection: React.FC<SettingsSectionProps> = ({ reloadKey }) => {
    const record = useRecordContext();
    if (!record) return null;
    return <KioskSettingsPanel kioskId={String(record.id)} reloadKey={reloadKey} />;
};

export const KioskDeviceEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
    const [settingsReloadKey, setSettingsReloadKey] = useState(0);
    const [enrollmentDialog, setEnrollmentDialog] = useState<{ open: boolean; code: string; expiresAt: string | null }>({
        open: false,
        code: '',
        expiresAt: null,
    });

    return (
        <>
            <Edit
                title={false}
                actions={false}
                component="div"
                mutationMode="pessimistic"
                mutationOptions={{
                    onSuccess: () => {
                        setServerErrors({});
                        notify('ra.notification.updated', { type: 'info', messageArgs: { smart_count: 1 } });
                        redirect('list', 'kiosk-devices');
                    },
                    onError: (error: any) => {
                        const apiError = error?.body || error;
                        const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                        const fieldErrors: Record<string, string> = {};
                        const summaryLines: string[] = [];
                        if (rawErrors) {
                            for (const [path, msgs] of Object.entries(rawErrors)) {
                                const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                                const translated = translateServerMessage(rawMsg, translate);
                                const field = mapKioskBackendPath(path);
                                fieldErrors[field] = translated;
                                const label = translate(kioskFieldLabelKey(field), { _: field });
                                summaryLines.push(`• ${label}: ${translated}`);
                            }
                        }
                        setServerErrors(fieldErrors);
                        const header = kioskErrorHeader(apiError, translate);
                        if (summaryLines.length > 0) {
                            notify(`${header}\n${summaryLines.join('\n')}`, { type: 'error', multiLine: true, autoHideDuration: 10000 });
                        } else {
                            notify(header, { type: 'error', autoHideDuration: 6000 });
                        }
                    },
                }}
                sx={{ maxWidth: '100%', overflow: 'hidden', backgroundColor: 'transparent', '& .RaEdit-main': { backgroundColor: 'transparent', boxShadow: 'none' } }}
            >
                <SimpleForm
                    toolbar={<EditToolbar />}
                    sx={{
                        '& .MuiPaper-root': { backgroundColor: 'transparent', boxShadow: 'none' },
                        '& > .MuiCardContent-root, & > div > .MuiCardContent-root': { px: { xs: 0, sm: 2 }, py: { xs: 1, sm: 2 } },
                        padding: { xs: 0, sm: 'inherit' },
                    }}
                >
                    <ServerErrorBinder errors={serverErrors} />
                    <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                        <PageHeader />

                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Storefront sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.kiosk-devices.sections.basicInfo')}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'grid', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{translate('resources.kiosk-devices.fields.siteId')}</Typography>
                                        <ReferenceField source="siteId" reference="sites" link={false}>
                                            <TextField source="name" />
                                        </ReferenceField>
                                    </Box>

                                    <TextInput
                                        source="name"
                                        label={translate('resources.kiosk-devices.fields.name')}
                                        validate={required()}
                                        fullWidth
                                        helperText={translate('resources.kiosk-devices.create.nameHelperText')}
                                    />

                                    <TextInput
                                        source="octopusDeviceId"
                                        label={translate('resources.kiosk-devices.fields.octopusDeviceId')}
                                        fullWidth
                                        helperText={translate('resources.kiosk-devices.create.octopusDeviceIdHelperText')}
                                    />

                                    <TextInput
                                        source="locationLabel"
                                        label={translate('resources.kiosk-devices.fields.locationLabel')}
                                        fullWidth
                                        inputProps={{ maxLength: 80 }}
                                        helperText={translate('resources.kiosk-devices.create.locationLabelHelperText')}
                                    />
                                </Box>
                            </CardContent>
                        </Card>

                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <ElectricBolt sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.kiosk-devices.sections.hardware')}
                                    </Typography>
                                </Box>
                                <NearbyChargersInput />
                            </CardContent>
                        </Card>

                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Info sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.kiosk-devices.sections.status')}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'grid', gap: 2 }}>
                                    <StatusInput />
                                    <MaintenanceSwitch onChanged={() => setSettingsReloadKey((k) => k + 1)} />
                                </Box>
                            </CardContent>
                        </Card>

                        <StatusReadOnlyCard />

                        <OctopusReaderPanel />

                        <KioskRecentAlertsPanel />

                        <KioskUpdatePanel />

                        <KioskPrintLogPanel />

                        <OctopusBlockedPanel />

                        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                            <EnrollmentSection
                                onCodeIssued={(code, expiresAt) => setEnrollmentDialog({ open: true, code, expiresAt })}
                            />
                        </Box>

                        <SettingsSection reloadKey={settingsReloadKey} />
                    </Box>
                </SimpleForm>
            </Edit>
            <EnrollmentCodeDialog
                open={enrollmentDialog.open}
                code={enrollmentDialog.code}
                expiresAt={enrollmentDialog.expiresAt}
                onClose={() => setEnrollmentDialog((s) => ({ ...s, open: false }))}
            />
        </>
    );
};
